import { AutogenLayoutPayRate401kSlideout } from "./autogen/AutogenLayoutPayRate401kSlideout";
import { Currency, CurrencySettings, ModelRow } from "@mcleod/core";
import {
    AddType, DataSource, DataSourceMode, DataSourceValidationEvent, DropdownSelectionEvent, EditRowDecorator, Layout,
    LookupModelSearchEvent, Table, TableAddRowResult, TableRow, ValidationResult
} from "@mcleod/components";
import { Payee401kRateDetails } from "./Payee401kRateDetails";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";
import { Payee401kRates } from "./Payee401kRates";

export class PayRate401kSlideout extends AutogenLayoutPayRate401kSlideout {
    private allRateData: DataSource;
    private birthDate: Date | null = null;
    private doBeforeSlideout: () => Promise<void>;

    async onLoad(): Promise<void> {
        // Add validation listener
        this.sourcePayee401kRate.addValidationListener((event: DataSourceValidationEvent) => {
            this.validatePayRate(event);
        });
        this.textboxDeductCodeId.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            event.filter["code_type"] = "in D,B";
        });
    }

    private validatePayRate(event?: DataSourceValidationEvent): ValidationResult[] {
        const activeRow = this.sourcePayee401kRate.activeRow;
        const errors: string[] = [];

        // Validation 1: Expiration cannot be before the effective date
        this.validateDateRange(activeRow, errors);

        // Validation 2: A 401(k) detail rate record is required
        this.validateDetailRecordsRequired(errors);

        // Validation 3: Prevents the overlap of effective and expiration dates with other 401(k) rates
        // for the payee AND contribution type combination
        this.validateDateOverlap(activeRow, errors);

        // Validation 4: Validate catch-up contribution eligibility
        this.validateCatchupContribution(activeRow, errors);

        // Add validation results to the event
        if (event) {
            errors.forEach(errorMessage => {
                event.validationResults.push({
                    isValid: false,
                    component: this,
                    caption: errorMessage,
                    validationMessage: errorMessage
                });
            });
            return null;
        } else {
            // create a ValidationResult array to return
            const validationResults: ValidationResult[] = [];
            errors.forEach(errorMessage => {
                validationResults.push({
                    isValid: false,
                    component: this,
                    caption: errorMessage,
                    validationMessage: errorMessage
                });
            });
            return validationResults;
        }
    }

    private validateDateRange(activeRow: ModelRow, errors: string[]): void {
        const effectiveDate = activeRow.get("effective_date");
        const expireDate = activeRow.get("expire_date");

        if (effectiveDate && expireDate) {
            const effective = new Date(effectiveDate);
            const expire = new Date(expireDate);

            if (expire < effective) {
                errors.push("Expiration Date cannot be before the Effective Date.");
            }
        }
    }

    private validateDetailRecordsRequired(errors: string[]): void {
        const detailRecords = this.layoutPayRateDetail.sourceDetail401kRate?.data || [];

        if (!detailRecords || detailRecords.length === 0) {
            errors.push("A 401(k) fund record is required.");
        }
    }

    private validateDateOverlap(activeRow: ModelRow, errors: string[]): void {
        const payeeId = activeRow.get("id");
        const contribType = activeRow.get("contrib_type");
        const effectiveDate = activeRow.getDate("effective_date");
        const expireDate = activeRow.getDate("expire_date");

        if (!this.allRateData || !this.allRateData.data) {
            return;
        }

        // Check for overlapping date ranges with existing records
        const existingRecords = this.allRateData.data as ModelRow[];

        // Check for date overlaps with existing records
        for (const existingRecord of existingRecords) {
            // Only check records with the same payee and contribution type
            if (existingRecord.get("id") !== payeeId || existingRecord.get("contrib_type") !== contribType || existingRecord.get("unique_id") === activeRow.get("unique_id")) {
                continue;
            }

            // Convert dates for overlap checking
            // Condition 2: A null effective or expiration date means the record is active for all time in either direction
            const existingEffective = existingRecord.getDate("effective_date");
            const existingExpire = existingRecord.getDate("expire_date");

            // Condition 3: Dates cannot overlap between records for the same contribution type
            if (this.datesOverlap(effectiveDate, expireDate, existingEffective, existingExpire)) {
                errors.push(`Date range overlaps with an existing 401(k) rate for the same payee and contribution type.`);
                break; // Only show first overlap found
            }
        }
    }

    private validateCatchupContribution(activeRow: ModelRow, errors: string[]): void {
        const contribType = activeRow.get("contrib_type");

        // Constants from RowPayee401kRate.java
        const CONTRIB_401K = "4";
        const CONTRIB_ROTH = "R";
        const CONTRIB_CATCHUP = "C";
        const CONTRIB_ROTH_CU = "T";
        const CATCHUP_AGE_MIN = 50;

        // If contribution type is empty, skip validation
        if (!contribType) {
            return;
        }

        // If contribution type is regular 401(k) or Roth, no validation needed
        if (contribType === CONTRIB_401K || contribType === CONTRIB_ROTH) {
            return;
        }

        // For catch-up contributions (C or T), validate age eligibility
        if (contribType === CONTRIB_CATCHUP || contribType === CONTRIB_ROTH_CU) {
            if (!this.birthDate) {
                errors.push("Payee must be 50 years old before being eligible for Catch-up contributions. Payee's birth date is not entered on the Payee screen to verify age.");
                return;
            }

            try {
                const effectiveDate: Date = activeRow.getDate("effective_date", new Date());
                if (!effectiveDate) {
                    return;
                }

                // Calculate the payee's 50th birthday
                const fiftiethBirthday = new Date(this.birthDate);
                fiftiethBirthday.setFullYear(this.birthDate.getFullYear() + CATCHUP_AGE_MIN);

                // Check if payee is at least 50 years old on the effective date
                if (effectiveDate.getFullYear() < fiftiethBirthday.getFullYear()) {
                    errors.push("Payee will not be 50 years old by the end of the effective year, and therefore is ineligible for catch-up contributions.");
                }
            } catch (error) {
                errors.push("Error with Effective date validation for catch-up contributions.");
            }
        }
    }

    private datesOverlap(start1: Date, end1: Date | null, start2: Date, end2: Date | null): boolean {
        // if value is null, treat it as an infinite date range
        start1 = start1 || new Date(0); // Default to epoch if null
        end1 = end1 || new Date(8640000000000000); // Default to max date if null
        start2 = start2 || new Date(0); // Default to epoch if null
        end2 = end2 || new Date(8640000000000000); // Default to max date if null

        // Two date ranges overlap if:
        // start1 <= actualEnd2 AND start2 <= actualEnd1
        return start1 <= end2 && start2 <= end1;
    }

    /**
     * Generic static method to open the PayRate401kSlideout
     * @param mode - "add" or "update" mode
     * @param options - Configuration options for the slideout
     */
    public static async openSlideout(
        mode: "add" | "update" = "add",
        options: {
            selectedRow?: TableRow;
            parentDataSource: DataSource;
            sourcePayee401kRate: DataSource;
            tablePayee401kRate: Table;
            rateDetailsMap: Map<string, Payee401kRateDetails>;
        }
    ): Promise<EditRowDecorator> {
        const {
            selectedRow,
            parentDataSource,
            sourcePayee401kRate,
            tablePayee401kRate,
            rateDetailsMap
        } = options;

        const layout = Layout.getLayout("lme/settlement/PayRate401kSlideout") as PayRate401kSlideout;
        await layout.ensureLoaded();
        layout.layoutPayRateDetail.table401kDetails.addType = AddType.QUICK;
        layout.layoutPayRateDetail.table401kDetails.allowEdit = true;
        layout.layoutPayRateDetail.table401kDetails.allowDelete = true;
        layout.layoutPayRateDetail.table401kDetails.displayToolsCell = true;
        layout.allRateData = sourcePayee401kRate;
        layout.birthDate = parentDataSource.activeRow.getDate("payee.birth_date", parentDataSource.activeRow.getDate("birth_date"));
        layout.textboxCalculationMethod.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => {
            if (event.hasSelection()) {
                if (layout.layoutPayRateDetail.rateRow) {
                    layout.layoutPayRateDetail.rateRow.set("calculation_method", layout.textboxCalculationMethod.getDataValue());
                }
                layout.layoutPayRateDetail.setFieldVisibility(layout.layoutPayRateDetail.table401kDetails.quickAddRow, layout.textboxCalculationMethod.getDataValue());
                for (const row of layout.layoutPayRateDetail.table401kDetails.rows) {
                    layout.layoutPayRateDetail.setFieldVisibility(row, layout.textboxCalculationMethod.getDataValue());
                }
            }
        });
        const catchupEnabled = parentDataSource.activeRow.getBoolean("enable_catchup", false);
        const rothEnabled = parentDataSource.activeRow.getBoolean("enable_roth", false);
        if (!catchupEnabled) {
            layout.textboxContribType.removeItemsWithValue("C");
            layout.textboxContribType.removeItemsWithValue("T");
        }
        if (!rothEnabled) {
            layout.textboxContribType.removeItemsWithValue("R");
            layout.textboxContribType.removeItemsWithValue("T");
        }
        let data: ModelRow;
        layout.layoutPayRateDetail.table401kDetails.dataSource.parentDataSource = new DataSource(); // Fake parent to avoid "are you sure" dialog
        if (mode === "add") {
            data = await sourcePayee401kRate.createBlankRow();
            data.set("contrib_type", layout.textboxContribType.resolveItems()?.[0]?.value);
            data.set("payee.birth_date", layout.birthDate);
            data.set("category", "D");
            layout.layoutPayRateDetail.initialize(data.createBasicCopy(), []);
        } else {
            if (!selectedRow) {
                throw new Error("Selected row is required for update mode");
            }
            const sourceDetail401kRate = rateDetailsMap.get(selectedRow.data.get("unique_id")).sourceDetail401kRate;
            const copyData = sourceDetail401kRate.getDataCopy();
            const selectedRowData = selectedRow.data.createBasicCopy();
            layout.layoutPayRateDetail.initialize(selectedRowData, copyData || []);
            data = selectedRowData;
            selectedRow.setExpandedAsync(false);
        }

        return new EditRowDecorator({
            layout: layout,
            title: `${mode === "add" ? "Add" : "Update"} 401(k) Rate`,
            dataSourceMode: mode === "add" ? DataSourceMode.ADD : DataSourceMode.UPDATE,
            data: data,
            width: "75%",
            onValidate: () => {
                const validationResults = layout.validatePayRate();
                if (validationResults && validationResults.length > 0) {
                    ValidationUtil.showFailedValidationDialog(validationResults, "Unable to save this record because data is missing or invalid.");
                }
                return validationResults;
            },
            onSave: (updatedData: ModelRow) => {
                updatedData.set("rate_details", layout.layoutPayRateDetail.sourceDetail401kRate.data || []);
                if (mode === "add") {
                    const result: TableAddRowResult = tablePayee401kRate.addRow(updatedData, null, {
                        display: true,
                        addToData: true
                    });
                    layout.recalculateTotals(result.row).then(() => {
                        result.row.displayComponentValues();
                    });
                } else {
                    if (selectedRow) {
                        const rateDetailLayout = rateDetailsMap.get(selectedRow.data.get("unique_id"));
                        if (rateDetailLayout) {
                            rateDetailLayout.replaceMainDatasource(layout.layoutPayRateDetail.sourceDetail401kRate);
                        }
                        selectedRow.saveChangesFromEdit(updatedData);
                        layout.recalculateTotals(selectedRow).then(() => {
                            selectedRow.displayComponentValues();
                        });
                    }
                }
            },
            onClose: (cancelled: boolean) => {
                layout.slideOut().then(() => {
                    selectedRow?.setExpandedAsync(true);
                });
            }
        });
    }

    private async recalculateTotals(selectedRow: TableRow): Promise<void> {
        const transactionalCurrency = CurrencySettings.getTransactionalCurrency();
        const rateDetails = this.layoutPayRateDetail.table401kDetails.rows;
        let totalPayeePercent = 0;
        const totalPayeeAmount: Currency = new Currency({amount:0, currency_code: transactionalCurrency});
        let totalEmployerPercent = 0;
        const totalEmployerAmount: Currency = new Currency({amount:0, currency_code: transactionalCurrency});
        const calculationMethod = this.textboxCalculationMethod.selectedItem?.value

        for (const row of rateDetails) {
            const data = row.data;
            if (calculationMethod === "P") {
                totalPayeePercent += Number.parseFloat(data.get("payee_percent", "0"));
                totalEmployerPercent += Number.parseFloat(data.get("match_percent", "0"));
            } else if (calculationMethod === "F") {
                await totalPayeeAmount.add(data.getCurrency("payee_amount", 0));
                await totalEmployerAmount.add(data.getCurrency("match_amount", 0));
            }
        }

        selectedRow.data.set("total_payee_percent", totalPayeePercent);
        selectedRow.data.set("total_payee_amount", totalPayeeAmount);
        selectedRow.data.set("total_employer_percent", totalEmployerPercent);
        selectedRow.data.set("total_employer_amount", totalEmployerAmount);
        Payee401kRates.setTotalsFieldVisibility(selectedRow);
    }
}
